import React, { createContext, useContext, useState, useEffect } from 'react';
import frTranslations from './translations/fr.json';
import enTranslations from './translations/en.json';

const LanguageContext = createContext();

const translations = {
  fr: frTranslations,
  en: enTranslations,
};

const STORAGE_KEY = 'app_language';
const VALID_LANGS = ['fr', 'en'];

function getStoredLanguage() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (VALID_LANGS.includes(saved)) return saved;
  } catch (e) {}
  return 'fr';
}

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(getStoredLanguage);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, language);
    } catch (e) {}
  }, [language]);

  // Sync language if user changes it in another tab
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY && VALID_LANGS.includes(e.newValue)) {
        setLanguage(e.newValue);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const t = (key, params = {}) => {
    const keys = key.split('.');
    const lang = translations[language];

    // Navigate the current language object
    let value = lang;
    for (const k of keys) {
      if (value && typeof value === 'object' && k in value) {
        value = value[k];
      } else {
        // Key not found in current language — return the key so callers
        // like tr(key, fallback) can use their hardcoded fallback string.
        return key;
      }
    }

    if (typeof value !== 'string') return key;

    // Replace {{param}} placeholders if any
    if (Object.keys(params).length > 0) {
      return value.replace(/\{\{(\w+)\}\}/g, (match, p) =>
        params[p] !== undefined ? params[p] : match
      );
    }

    return value;
  };

  const changeLanguage = (lang) => {
    if (VALID_LANGS.includes(lang)) {
      setLanguage(lang);
      try {
        localStorage.setItem(STORAGE_KEY, lang);
      } catch (e) {}
    }
  };

  return (
    <LanguageContext.Provider value={{ language, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
